/**
 * AdminTickets - Support ticket management for admin team
 * View all tickets across clients, assign, respond, close
 */

import React, { useState, useEffect } from 'react';
import AdminShell from './AdminShell';
import { adminFetch, getAdminData, hasPermission } from './api';

const PRIORITY_COLORS = { urgent: '#EF4444', high: '#F59E0B', normal: '#3B82F6', low: '#6B7280' };
const STATUS_COLORS = { open: '#F59E0B', in_progress: '#3B82F6', waiting: '#C084FC', resolved: '#10B981', closed: '#6B7280' };

function formatDate(d) {
  if (!d) return '-';
  const date = new Date(d);
  const diff = Date.now() - date;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const admin = getAdminData();

  useEffect(() => {
    adminFetch('/api/admin/tickets')
      .then(r => r.json())
      .then(d => { setTickets(d.tickets || []); setLoading(false); })
      .catch(() => { setTickets([]); setLoading(false); });
  }, []);

  const filtered = filter === 'all' ? tickets : tickets.filter(t => t.status === filter);

  const s = {
    topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
    filters: { display: 'flex', gap: 8 },
    filterBtn: (active) => ({
      padding: '8px 16px', borderRadius: 20, border: '1px solid #2A3444', fontSize: 13,
      fontWeight: 500, cursor: 'pointer',
      backgroundColor: active ? '#1E293B' : 'transparent',
      color: active ? '#FFF' : '#8B92A5',
    }),
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '12px 16px', fontSize: 11, fontWeight: 700, color: '#5A6175', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2A3444' },
    td: { padding: '14px 16px', fontSize: 14, color: '#BBBEC7', borderBottom: '1px solid #1A2332' },
  };

  const openCount = tickets.filter(t => t.status === 'open').length;
  const urgentCount = tickets.filter(t => t.priority === 'urgent' && t.status !== 'closed').length;

  return (
    <AdminShell title="Support Tickets">
      <div style={s.topBar}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <span style={{ fontSize: 14, color: '#FFF', fontWeight: 600 }}>{openCount} open</span>
          {urgentCount > 0 && (
            <span style={{ fontSize: 13, color: '#EF4444', fontWeight: 600 }}>{urgentCount} urgent</span>
          )}
        </div>
        <div style={s.filters}>
          {['all', 'open', 'in_progress', 'waiting', 'resolved', 'closed'].map(f => (
            <button key={f} style={s.filterBtn(filter === f)} onClick={() => setFilter(f)}>
              {f === 'in_progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div style={{ backgroundColor: '#1A2332', borderRadius: 12, border: '1px solid #2A3444', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#5A6175' }}>Loading tickets...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#5A6175' }}>
            {tickets.length === 0 ? 'No support tickets yet. Tickets created by clients will appear here.' : 'No tickets match this filter.'}
          </div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Subject</th>
                <th style={s.th}>Client</th>
                <th style={s.th}>Priority</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Category</th>
                <th style={s.th}>Created</th>
                <th style={s.th}>Updated</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => {
                const pColor = PRIORITY_COLORS[t.priority] || '#6B7280';
                const sColor = STATUS_COLORS[t.status] || '#6B7280';
                return (
                  <tr key={t.id}>
                    <td style={s.td}>
                      <div style={{ fontWeight: 600, color: '#FFF' }}>{t.subject}</div>
                      <div style={{ fontSize: 12, color: '#5A6175', marginTop: 2 }}>#{t.ticket_number || t.id}</div>
                    </td>
                    <td style={s.td}>{t.company_name || '-'}</td>
                    <td style={s.td}>
                      <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', backgroundColor: `${pColor}20`, color: pColor }}>{t.priority}</span>
                    </td>
                    <td style={s.td}>
                      <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, backgroundColor: `${sColor}20`, color: sColor }}>{t.status?.replace('_', ' ')}</span>
                    </td>
                    <td style={s.td}>{t.category || '-'}</td>
                    <td style={s.td}>{formatDate(t.created_at)}</td>
                    <td style={s.td}>{formatDate(t.updated_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
}
